import { useAuth } from "@/src/contexts/auth.context";
import { countersEnabled, recountPost } from "@/src/services/api/posts/engagement-functions";
import { decodeToken } from "@/src/services/api/auth.api";
import {
  GetUserProfilesResponse,
  InsertUserProfileInput,
  InsertUserProfileResponse,
  UpdateUserProfileResponse,
  UserProfileItem,
} from "@/src/models/user.model";
import { NEWS_SAVED_QUERY_KEY } from "@/src/query-keys/news.keys";
import { POSTS_QUERY_KEY } from "@/src/query-keys/post.keys";
import { USER_PROFILE_QUERY_KEYS } from "@/src/query-keys/users.keys";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Toast from "react-native-toast-message";
import gqlClient from "../api/graphql.client";
import {
  profileOwnerFilter,
  GET_USER_PROFILES,
  GET_USER_PROFILE_COUNTRIES,
  INSERT_USER_PROFILE,
  UPDATE_POST_SAVE_COUNT_MUTATION,
  CREATE_USER_PREFERENCES_MUTATION,
  GET_USER_PREFERENCES_QUERY,
  GET_ALL_USER_PROFILES,
  UPDATE_USER_PREFERENCES_MUTATION,
  UPDATE_USER_PROFILE,
  UPDATE_USER_SAVED_POSTS_MUTATION,
  GET_PREFERRED_ORG_QUERY,
  CLEAR_PREFERRED_ORG_MUTATION,
  UPDATE_PREFERRED_ORG_MUTATION,
} from "./profile.query";
import { queryClient } from "@/src/config/react-query";

/**
 * Return the caller's `userprofile` row, creating it if absent.
 *
 * Everything the app persists per-user — saved posts, saved news/wiki, news and
 * wiki draft ids — lives on this one row. When it does not exist, the writes fail
 * **silently**: `UPDATE_USER_PROFILE` matches on `CreatedBy`, so with no row it
 * updates zero records, still reports `acknowledged`, and nobody inspects
 * `totalImpactedData`. The user saves a post or a draft, sees no error, and the
 * value is simply gone on the next load.
 *
 * This mirrors the `fetchOrCreateProfile` that `useUserProfileState` already used
 * privately; it is promoted here so the save-post and draft-tracking paths, which
 * bypassed it, cannot silently no-op either.
 */
export const ensureUserProfile = async (
  userId: string,
): Promise<UserProfileItem | null> => {
  if (!userId) return null;
  const client = await gqlClient();

  const read = async () => {
    const data = await client.request<GetUserProfilesResponse>(
      GET_USER_PROFILES,
      { where: profileOwnerFilter(userId), order: [], pageNo: 1, pageSize: 1 },
    );
    return data.getuserprofiles.items[0] ?? null;
  };

  // The read above uses profileOwnerFilter, so it finds a row created by the user
  // OR one created on their behalf (the import). That is what stops this from
  // inserting a second profile for an imported user on their first sign-in.
  const existing = await read();
  if (existing) return existing;

  await client.request(INSERT_USER_PROFILE, {
    // Written explicitly from here on, so ownership stops depending on who
    // happened to make the request. Existing rows have it null, which is why
    // profileOwnerFilter still accepts CreatedBy.
    userId,
    wikiSaved: [],
    newsSaved: [],
    newsRead: [],
    wikiDraftIds: [],
    newsDraftIds: [],
  });

  return read();
};

const useInvalidateUserProfiles = () => {
  const queryClient = useQueryClient();
  return () =>
    queryClient.invalidateQueries({ queryKey: USER_PROFILE_QUERY_KEYS.base });
};

export const useGetUserProfileQuery = (userId: string) =>
  useQuery<UserProfileItem | null>({
    queryKey: USER_PROFILE_QUERY_KEYS.byUserId(userId),
    queryFn: async () => {
      const client = await gqlClient();
      const data = await client.request<GetUserProfilesResponse>(
        GET_USER_PROFILES,
        {
          where: profileOwnerFilter(userId),
          order: [],
          pageNo: 1,
          pageSize: 1,
        },
      );
      return data.getuserprofiles.items[0] ?? null;
    },
    enabled: !!userId,
  });

export type UserCountriesData = {
  countryByUserId: Record<string, string>;
  countries: string[];
};

/** Page size for the country lookup. Small enough to keep each request light. */
const COUNTRY_PROFILES_PAGE_SIZE = 500;

/**
 * The user ids whose profile country is `country`.
 *
 * Replaces a hook that downloaded every profile in the org (`where: {}`,
 * `pageSize: 1000`) and matched countries in memory. That had two faults: it ran
 * whenever the invite modal opened, whether or not anyone touched the filter, and
 * it silently omitted people once an org held more than 1000 profiles — a country
 * selection would simply miss them.
 *
 * Filtering server-side fixes both. `enabled: !!country` keeps it off the
 * modal-open path, and paging to `totalCount` removes the ceiling.
 *
 * Ownership comes from `userId` when set and `CreatedBy` otherwise — see
 * profileOwnerFilter. Imported profiles are created by an admin, so `CreatedBy`
 * alone would attribute every one of them to that admin.
 */
export const useUserIdsByCountry = (country: string) =>
  useQuery<string[]>({
    queryKey: [...USER_PROFILE_QUERY_KEYS.base, "idsByCountry", country],
    enabled: !!country,
    queryFn: async () => {
      const client = await gqlClient();
      const userIds = new Set<string>();

      let pageNo = 1;
      let totalCount = 0;

      do {
        const data = await client.request<{
          getuserprofiles: {
            totalCount: number;
            items: {
              CreatedBy: string | null;
              userId: string | null;
              country: string | null;
            }[];
          };
        }>(GET_USER_PROFILE_COUNTRIES, {
          where: { country: { eq: country } },
          order: [],
          pageNo,
          pageSize: COUNTRY_PROFILES_PAGE_SIZE,
        });

        const page = data.getuserprofiles;
        totalCount = page.totalCount ?? 0;

        // An empty page also ends the loop, so a totalCount the gateway does not
        // populate cannot spin forever.
        if (!page.items.length) break;

        for (const item of page.items) {
          const owner = item.userId ?? item.CreatedBy;
          if (owner) userIds.add(owner);
        }

        pageNo += 1;
      } while (userIds.size < totalCount);

      return [...userIds];
    },
  });

/**
 * Shape of the `preferences` nested object as it actually comes back from
 * GET_USER_PREFERENCES_QUERY — field names as the schema spells them
 * (`language`, not the `UserPreferences.langage` typo carried in
 * user.model.ts for the older, unused type).
 */
export type UserPreferencesQueryResult = {
  language?: string | null;
  lightMode?: boolean | null;
  notificationsEnabled?: boolean | null;
};

export const useGetUserPreferencesQuery = (userId: string) =>
  useQuery<UserPreferencesQueryResult | null>({
    queryKey: ["userPreferences", userId],
    queryFn: async () => {
      const client = await gqlClient();
      const data = await client.request<{
        getuserprofiles: {
          items: { preferences: UserPreferencesQueryResult | null }[];
        };
      }>(GET_USER_PREFERENCES_QUERY, {
        where: profileOwnerFilter(userId),
        order: [],
      });
      return data.getuserprofiles.items[0]?.preferences ?? null;
    },
    enabled: !!userId,
  });

export const useUpdateUserPreferences = () => {
  return useMutation({
    mutationFn: async ({
      userId,
      ...preferences
    }: {
      userId: string;
      language?: string;
      lightMode?: boolean;
      notificationsEnabled?: boolean;
    }) => {
      const client = await gqlClient();
      return client.request(UPDATE_USER_PREFERENCES_MUTATION, {
        where: profileOwnerFilter(userId),
        ...preferences,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["userPreferences"] });
    },
  });
};

export const useCreateUserPreferences = () => {
  return useMutation({
    mutationFn: async (variables: {
      language: string;
      lightMode: boolean;
      notificationsEnabled?: boolean;
    }) => {
      const client = await gqlClient();
      return client.request(CREATE_USER_PREFERENCES_MUTATION, variables);
    },
  });
};

export const useInsertUserProfileMutation = () => {
  const invalidate = useInvalidateUserProfiles();
  return useMutation({
    mutationFn: async (input: InsertUserProfileInput) => {
      const client = await gqlClient();
      const data = await client.request<InsertUserProfileResponse>(
        INSERT_USER_PROFILE,
        input,
      );
      return data.insertuserprofile;
    },
    onSuccess: () => invalidate(),
  });
};

export const useUpdateUserProfileMutation = () => {
  const invalidate = useInvalidateUserProfiles();
  return useMutation({
    mutationFn: async ({
      input,
      userId,
    }: {
      input: InsertUserProfileInput;
      userId: string;
    }) => {
      const client = await gqlClient();
      const data = await client.request<UpdateUserProfileResponse>(
        UPDATE_USER_PROFILE,
        {
          where: profileOwnerFilter(userId),
          ...input,
        },
      );

      const result = data.updateuserprofile;

      // `acknowledged: true` is not success. A write that matched no row reports
      // exactly that, with `totalImpactedData: 0` — which is how profile edits
      // disappeared silently and looked like a UI bug. Failing here puts it in
      // front of the user instead, and surfaces `message` when the gateway
      // bothered to say why.
      if (!result?.acknowledged || (result?.totalImpactedData ?? 0) === 0) {
        throw new Error(
          result?.message ??
            "The profile was not saved: the update matched no record.",
        );
      }

      return result;
    },
    onSuccess: () => {
      invalidate();
    },
  });
};

export const useToggleSavePostMutation = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const userId = user?.itemId ?? "";

  return useMutation({
    mutationFn: async ({
      userProfileId,
      savedPostIds,
      postId,
      postSaveCount,
    }: {
      userProfileId: string;
      savedPostIds: string[];
      postId: string;
      postSaveCount: number;
    }) => {
      const client = await gqlClient();
      await client.request(UPDATE_USER_SAVED_POSTS_MUTATION, {
        itemId: userProfileId,
        savedPostIds,
      });

      const isSaving = savedPostIds.includes(postId);
      if (countersEnabled) {
        await recountPost(postId);
        return;
      }
      await client.request(UPDATE_POST_SAVE_COUNT_MUTATION, {
        itemId: postId,
        saveCount: isSaving
          ? (postSaveCount ?? 0) + 1
          : Math.max(0, (postSaveCount ?? 0) - 1),
      });
    },

    onMutate: async ({
      savedPostIds,
      postId,
      postSaveCount,
    }): Promise<{ previousProfile: unknown }> => {
      await queryClient.cancelQueries({
        queryKey: USER_PROFILE_QUERY_KEYS.byUserId(userId),
      });

      const previousProfile = queryClient.getQueryData(
        USER_PROFILE_QUERY_KEYS.byUserId(userId),
      );

      queryClient.setQueryData(
        USER_PROFILE_QUERY_KEYS.byUserId(userId),
        (old: any) => {
          if (!old) return old;
          return { ...old, savedPostIds };
        },
      );

      const isSaving = savedPostIds.includes(postId);
      // Optimistically remove post from saved list if unsaving
      if (!isSaving) {
        queryClient.setQueryData(
          [POSTS_QUERY_KEY, "saved", userId],
          (old: any) => {
            if (!old) return old;
            return old.filter((post: any) => post.ItemId !== postId);
          },
        );
      }
      queryClient.setQueryData([POSTS_QUERY_KEY, ""], (old: any) => {
        if (!old?.pages) return old;
        return {
          ...old,
          pages: old.pages.map((page: any) => ({
            ...page,
            getposts: {
              ...page.getposts,
              items: page.getposts.items.map((post: any) => {
                if (post.ItemId !== postId) return post;
                return {
                  ...post,
                  saveCount: isSaving
                    ? (postSaveCount ?? 0) + 1
                    : Math.max(0, (postSaveCount ?? 0) - 1),
                };
              }),
            },
          })),
        };
      });

      queryClient.setQueryData([POSTS_QUERY_KEY, postId], (old: any) => {
        if (!old) return old;
        return {
          ...old,
          saveCount: isSaving
            ? (postSaveCount ?? 0) + 1
            : Math.max(0, (postSaveCount ?? 0) - 1),
        };
      });

      return { previousProfile };
    },

    onSuccess: (_data, { postId, savedPostIds }) => {
      const isSaving = savedPostIds.includes(postId);
      Toast.show({
        type: "success",
        text1: isSaving ? "SUCCESS_SAVE_POST" : "SUCCESS_UNSAVE_POST",
      });
    },

    onError: (
      _err,
      _variables,
      context: { previousProfile: unknown } | undefined,
    ) => {
      queryClient.setQueryData(
        USER_PROFILE_QUERY_KEYS.byUserId(userId),
        context?.previousProfile,
      );
      Toast.show({
        type: "error",
        text1: "ERROR_POST_SAVE_FAILED",
      });
    },

    onSettled: (_data, err) => {
      if (err) {
        queryClient.invalidateQueries({
          queryKey: USER_PROFILE_QUERY_KEYS.byUserId(userId),
        });
      }
      // Always invalidate saved posts query to sync the list
      queryClient.invalidateQueries({
        queryKey: [POSTS_QUERY_KEY, "saved", userId],
      });
    },
  });
};

const toggleId = (list: string[] = [], id: string): string[] =>
  list.includes(id) ? list.filter((i) => i !== id) : [...list, id];

export const useToggleSaved = (userId: string, type: "news" | "wiki") => {
  const queryClient = useQueryClient();
  const field = type === "news" ? "newsSaved" : "wikiSaved";
  const profileQueryKey = USER_PROFILE_QUERY_KEYS.byUserId(userId);

  return useMutation({
    mutationFn: async (_itemId: string) => {
      const currentProfile = queryClient.getQueryData<any>(profileQueryKey);
      const updated = Array.isArray(currentProfile?.[field])
        ? currentProfile[field]
        : [];

      const client = await gqlClient();
      await client.request(UPDATE_USER_PROFILE, {
        where: profileOwnerFilter(userId),
        [field]: updated,
      });
    },

    onMutate: async (itemId: string) => {
      await queryClient.cancelQueries({ queryKey: profileQueryKey });
      const previousProfile = queryClient.getQueryData(profileQueryKey);

      queryClient.setQueryData(profileQueryKey, (old: any) => {
        if (!old) return old;
        const current = Array.isArray(old[field]) ? old[field] : [];
        return { ...old, [field]: toggleId(current, itemId) };
      });

      return { previousProfile };
    },

    onError: (_err, _itemId, context) => {
      if (context?.previousProfile) {
        queryClient.setQueryData(profileQueryKey, context.previousProfile);
      }
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: profileQueryKey });
      queryClient.invalidateQueries({ queryKey: NEWS_SAVED_QUERY_KEY });
    },
  });
};

const addId = (list: string[] = [], id: string): string[] =>
  list.includes(id) ? list : [...list, id];

/**
 * Marks a news article as read for `userId`, mirroring `useToggleSaved`'s
 * mutation/optimistic-update shape but for `newsRead` — an idempotent ADD
 * only. Opening an article always marks it read; there is no "mark unread"
 * from the article itself, so there is no toggle here.
 */
export const useMarkNewsRead = (userId: string) => {
  const queryClient = useQueryClient();
  const profileQueryKey = USER_PROFILE_QUERY_KEYS.byUserId(userId);

  return useMutation({
    mutationFn: async (itemId: string) => {
      // The stored list comes from the server, not from the query cache, and the
      // opened article is added here rather than being taken on trust from the
      // optimistic write.
      //
      // Both of those used to be the other way round, and together they lost the
      // read state: the id was never added in this function at all, so the write
      // depended entirely on `onMutate` having already put it in the cache — and
      // `onMutate` bails when the profile query has not resolved yet, which is
      // the normal case when an article is opened straight from a link or a
      // reload. `newsRead: []` then went to the server and wiped every article
      // the user had read, which is why a pinned article reappeared in the feed
      // after being opened.
      const stored = await ensureUserProfile(userId);
      const storedRead: string[] = Array.isArray(stored?.newsRead)
        ? stored.newsRead
        : [];
      const cachedProfile = queryClient.getQueryData<any>(profileQueryKey);
      const cachedRead: string[] = Array.isArray(cachedProfile?.newsRead)
        ? cachedProfile.newsRead
        : [];
      const updated = addId(
        [...new Set([...storedRead, ...cachedRead])],
        itemId,
      );

      const client = await gqlClient();
      await client.request(UPDATE_USER_PROFILE, {
        where: profileOwnerFilter(userId),
        newsRead: updated,
      });
      return updated;
    },

    onMutate: async (itemId: string) => {
      await queryClient.cancelQueries({ queryKey: profileQueryKey });
      const previousProfile = queryClient.getQueryData(profileQueryKey);

      queryClient.setQueryData(profileQueryKey, (old: any) => {
        if (!old) return old;
        const current = Array.isArray(old.newsRead) ? old.newsRead : [];
        return { ...old, newsRead: addId(current, itemId) };
      });

      return { previousProfile };
    },

    onError: (_err, _itemId, context) => {
      if (context?.previousProfile) {
        queryClient.setQueryData(profileQueryKey, context.previousProfile);
      }
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: profileQueryKey });
    },
  });
};

export const useToggleDraft = (userId: string, type: "news" | "wiki") => {
  const queryClient = useQueryClient();
  const { data: profile } = useGetUserProfileQuery(userId);

  return useMutation({
    mutationFn: async (itemId: string) => {
      const client = await gqlClient();

      const field = type === "news" ? "newsDraftIds" : "wikiDraftIds";

      const current = Array.isArray(profile?.[field]) ? profile[field] : [];

      const updated = toggleId(current, itemId);

      await client.request(UPDATE_USER_PROFILE, {
        where: profileOwnerFilter(userId),
        [field]: updated,
      });
    },

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: USER_PROFILE_QUERY_KEYS.byUserId(userId),
      });
    },
  });
};

/**
 * Fetches only `preferredOrganizationId` from the caller's user profile.
 * Returns the id string, null when the profile exists but has no preference,
 * or undefined when the query is disabled / loading.
 *
 * Enabled only when `!orgSelected` so it runs exactly once after login and is
 * skipped on subsequent app opens where the org has already been selected.
 *
 * IMPORTANT: `preferredOrganizationId` is not yet synced to the Data Gateway
 * schema — run `madp run blocks-data-gateway` (blocked on authentication; see
 * open item 5 in V4-MIGRATION-STATUS.md). Until then this query fails GraphQL
 * schema validation on every call. `retry: 0` prevents the global `retry: 1`
 * from forcing two doomed round-trips before the auth redirect fires, which
 * caused the login-to-picker hang in CR-B2-02. A failed query is treated as
 * "no preference" in auth.context.tsx. Remove this comment once the field exists.
 */
export const useGetPreferredOrgQuery = (
  userId: string,
  options?: { enabled?: boolean },
) =>
  useQuery<string | null>({
    queryKey: USER_PROFILE_QUERY_KEYS.preferredOrgByUserId(userId),
    queryFn: async () => {
      const client = await gqlClient();
      const data = await client.request<{
        getuserprofiles: {
          items: { preferredOrganizationId?: string | null }[];
        };
      }>(GET_PREFERRED_ORG_QUERY, {
        where: profileOwnerFilter(userId),
      });
      return data.getuserprofiles.items[0]?.preferredOrganizationId ?? null;
    },
    enabled: (options?.enabled ?? true) && !!userId,
    staleTime: 0,
    // Must not retry — see comment above. Mirror of permissions.blocks.api.ts:260
    // and users.blocks.api.ts:533, both deliberately set to retry: 0 for auth-path queries.
    retry: 0,
  });

/**
 * Standalone function (not a hook) to clear a stale `preferredOrganizationId`.
 * Called fire-and-forget from the auth redirect effect when the stored preferred
 * org is no longer among the user's memberships.
 *
 * Errors are swallowed deliberately — a failure to clear the stale preference
 * does not affect the user's ability to reach the org picker.
 */
export const clearPreferredOrg = async (userId: string): Promise<void> => {
  try {
    const client = await gqlClient();
    await client.request(CLEAR_PREFERRED_ORG_MUTATION, { where: profileOwnerFilter(userId) });
  } catch {
    // Schema may not be deployed yet; failure is non-blocking.
  }
};

/**
 * Mutation to set (or clear) the user's preferred org on the UserProfile row.
 * This mutation is intentionally separate from the main profile update — while
 * preferredOrganizationId is absent from the Data Gateway schema (open item 5 in
 * V4-MIGRATION-STATUS.md), including it in the main INSERT/UPDATE mutation would
 * fail GraphQL schema validation and prevent all profile fields from saving.
 *
 * Callers handle mutation failure as partial success: the profile was saved but the
 * preference was not. Once the schema field exists, both paths work automatically.
 *
 * Unblock: madp run blocks-data-gateway (requires portal auth — see open item 5).
 */
export const useSetPreferredOrgMutation = () => {
  const invalidate = useInvalidateUserProfiles();
  return useMutation({
    mutationFn: async ({
      createdBy,
      preferredOrganizationId,
    }: {
      createdBy: string;
      preferredOrganizationId: string | null;
    }) => {
      const client = await gqlClient();
      const data = await client.request<{
        updateuserprofile: {
          acknowledged: boolean;
          totalImpactedData: number;
          itemId: string;
        };
      }>(UPDATE_PREFERRED_ORG_MUTATION, { where: profileOwnerFilter(createdBy), preferredOrganizationId });
      return data.updateuserprofile;
    },
    onSuccess: () => invalidate(),
  });
};

export const useGetAllUserProfilesQuery = () =>
  useQuery({
    queryKey: [...USER_PROFILE_QUERY_KEYS.base, "birthdays"],
    queryFn: async () => {
      const client = await gqlClient();
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const next30Days = new Date(today);
      next30Days.setDate(today.getDate() + 30);

      const currentMonth = String(today.getMonth() + 1).padStart(2, "0");

      const orgId = (await decodeToken())?.org_id;
      const data = await client.request<GetUserProfilesResponse>(
        GET_ALL_USER_PROFILES,
        { month: `<${currentMonth}>`, orgId },
      );

      return data.getuserprofiles.items
        .filter((p) => {
          if (!p.dob) return false;
          const match = p.dob.match(/(\d+)\/<(\d+)>\/(\d+)/);
          if (!match) return false;
          const [, day, month] = match;
          const bday = new Date(
            today.getFullYear(),
            Number(month) - 1,
            Number(day),
          );
          if (bday < today) bday.setFullYear(today.getFullYear() + 1);
          return bday <= next30Days;
        })
        .slice(0, 5);
    },
    staleTime: 0,
  });
